import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Backdrop,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Chip,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import { ExpandMore, Email, Phone, Add, Person } from '@mui/icons-material';
import NameChecker from '../../name_checker';
import SIC_CODES from '../sic_codes';
import OfficerAddDialog from './add_officer_dialog';
import AddressForm from './address_form';
import OfficerItem from './officer_item';

const CompanyInformation = ({ formData, handleInputChange, errors }) => {
  const [loading, setLoading] = useState(false);
  const [addOfficerDialogOpen, setAddOfficerDialogOpen] = useState(false);

  const onNameAvailable = (name) => {
    handleInputChange({ target: { name: 'companyName', value: name } });
  };

  const handleAddressChange = (field, value) => {
    handleInputChange({
      target: {
        name: 'registeredAddress',
        value: {
          ...formData.registeredAddress,
          [field]: value,
        },
      },
    });
  };

  const handleAddOfficer = (officerData) => {
    handleInputChange({
      target: {
        name: 'initialOfficers',
        value: [...formData.initialOfficers, officerData], // Append new officer
      },
    });
  };

  const handleUpdateOfficer = (index, updatedOfficerData) => {
    const updatedOfficers = formData.initialOfficers.map((officer, i) =>
      i === index ? updatedOfficerData : officer
    );

    handleInputChange({
      target: {
        name: 'initialOfficers',
        value: updatedOfficers,
      },
    });
  };

  const handleRemoveOfficer = (index) => {
    const updatedOfficers = formData.initialOfficers.filter(
      (_, i) => i !== index
    );
    handleInputChange({
      target: {
        name: 'initialOfficers',
        value: updatedOfficers,
      },
    });
  };

  const hasRegisteredAddress =
    !!formData.registeredAddress?.street &&
    !!formData.registeredAddress?.city &&
    !!formData.registeredAddress?.postalCode;

  return (
    <Box sx={{ mt: 3 }}>
      <Accordion expanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Company Details
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <NameChecker
                label="Company Name *"
                onNameAvailable={onNameAvailable}
                loading={loading}
                setLoading={setLoading}
                error={!!errors?.companyName}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                As registered with Companies House
              </Typography>
              {errors?.companyName && (
                <Typography color="error" variant="caption">
                  {errors.companyName}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={5}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Company Type *</InputLabel>
                <Select
                  name="companyType"
                  value={formData.companyType || ''}
                  onChange={handleInputChange}
                  label="Company Type *"
                >
                  <MenuItem value="Private Limited Company">
                    Private Limited Company (Ltd)
                  </MenuItem>
                  <MenuItem value="Limited Liability Partnership">
                    Limited Liability Partnership (LLP)
                  </MenuItem>
                  <MenuItem value="Public Limited Company">
                    Public Limited Company (PLC)
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={7}>
              <Autocomplete
                multiple
                options={SIC_CODES}
                getOptionLabel={(option) =>
                  `${option.code} - ${option.description}`
                }
                value={formData.sicCodes?.map(
                  (code) => SIC_CODES.find((sic) => sic.code === code) || code
                )}
                onChange={(event, newValue) => {
                  handleInputChange({
                    target: {
                      name: 'sicCodes',
                      value: newValue.map((item) =>
                        typeof item === 'string' ? item : item.code
                      ),
                    },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="SIC Codes *"
                    margin="normal"
                    placeholder={
                      formData.sicCodes?.length
                        ? ''
                        : 'Select at least one SIC code'
                    }
                    //helperText="Standard Industrial Classification codes"
                    error={!!errors?.sicCodes}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={typeof option === 'string' ? option : option.code}
                      {...getTagProps({ index })}
                      size="small"
                      key={index}
                    />
                  ))
                }
              />
              {errors?.sicCodes && (
                <Typography color="error" variant="caption">
                  {errors.sicCodes}
                </Typography>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Registered Office Address
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <AddressForm
              title="Registered Office Address"
              description="This will appear on public record"
              address={formData.registeredAddress}
              onAddressChange={handleAddressChange}
              showServiceAddressOption={true}
              errors={{
                street: errors?.['registeredAddress.street'],
                city: errors?.['registeredAddress.city'],
                postalCode: errors?.['registeredAddress.postalCode'],
              }}
            />
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Officers
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            //color={errors.directors ? 'red' : ''}
          >
            {formData.companyType === 'Private Limited Company'
              ? 'At least one director is required *'
              : formData.companyType === 'Public Limited Company'
                ? 'At least two directors and one secretary are required *'
                : 'LLPs require at least two designated members *'}
          </Typography>

          {formData.initialOfficers?.map((officer, index) => {
            return (
              <OfficerItem
                key={index}
                officer={officer}
                index={index}
                registeredAddress={formData.registeredAddress}
                onRemove={handleRemoveOfficer}
              />
            );
          })}

          <Tooltip
            title={
              hasRegisteredAddress
                ? 'Add a new Officer'
                : 'Fill in the registered address to add an officer.'
            }
          >
            <span>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setAddOfficerDialogOpen(true)}
                disabled={!hasRegisteredAddress}
                sx={{ mt: 1 }}
              >
                Add Officer
              </Button>
            </span>
          </Tooltip>

          <OfficerAddDialog
            companyAddress={formData.registeredAddress}
            open={addOfficerDialogOpen}
            onClose={() => setAddOfficerDialogOpen(false)}
            onAdd={handleAddOfficer}
          />

          {errors?.officers && (
            <Typography color="error" sx={{ mt: 2 }}>
              {errors.officers}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion expanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Share Structure
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Most companies have ordinary shares with £1 nominal value
          </Typography>

          {formData.ShareStructure?.shareClasses?.map((shareClass, index) => (
            <Box
              key={index}
              sx={{ mb: 3, p: 2, border: '1px solid #eee', borderRadius: 1 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Share type *</InputLabel>
                    <Select
                      value={shareClass.type}
                      // onChange={(e) =>
                      //   handleShareClassChange(index, 'type', e.target.value)
                      // }
                      label="Share type *"
                    >
                      <MenuItem value="ordinary">Ordinary</MenuItem>
                      <MenuItem value="preference">Preference</MenuItem>
                      <MenuItem value="redeemable">Redeemable</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Currency *"
                    value={shareClass.currency}
                    // onChange={(e) =>
                    //   handleShareClassChange(index, 'currency', e.target.value)
                    // }
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Value per share *"
                    type="number"
                    value={shareClass.valuePerShare}
                    // onChange={(e) =>
                    //   handleShareClassChange(
                    //     index,
                    //     'valuePerShare',
                    //     parseFloat(e.target.value)
                    //   )
                    // }
                    margin="normal"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">£</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Number of shares *"
                    type="number"
                    value={shareClass.totalShares}
                    // onChange={(e) =>
                    //   handleShareClassChange(
                    //     index,
                    //     'totalShares',
                    //     parseInt(e.target.value)
                    //   )
                    // }
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={shareClass.votingRights}
                        // onChange={(e) =>
                        //   handleShareClassChange(
                        //     index,
                        //     'votingRights',
                        //     e.target.checked
                        //   )
                        // }
                        name="votingRights"
                        color="primary"
                      />
                    }
                    label="Voting rights"
                    sx={{ mt: 2 }}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}

          <Button
            variant="outlined"
            startIcon={<Add />}
            //onClick={addShareClass}
            sx={{ mt: 1 }}
          >
            Add Share Class
          </Button>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Contact Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Full Name *"
                name="name"
                value={formData.name || ''}
                onChange={handleInputChange}
                margin="normal"
                error={!!errors?.name}
                helperText={errors?.name || 'Primary contact name'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Email address *"
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleInputChange}
                margin="normal"
                error={!!errors?.email}
                helperText={errors?.email || 'For official correspondence'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                // helperText="For official correspondence"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Phone number *"
                name="phone"
                value={formData.phone || ''}
                onChange={handleInputChange}
                margin="normal"
                error={!!errors?.phone}
                helperText={'Enter your UK phone number starting with +44 '}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      style={{ pointerEvents: 'none' }}
                    >
                      <Phone fontSize="small" />
                      {/* <Typography>+44</Typography> */}
                    </InputAdornment>
                  ),
                  inputProps: {
                    pattern: '[0-9]*',
                    inputMode: 'numeric',
                  },
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Backdrop open={loading} style={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default CompanyInformation;
